import {
  WizardButton,
  WizardButtonList,
  WizardContent,
  WizardInput,
  WizardInputList,
} from '../../../ui/wizard/wizard-content';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { RoutingPath } from '../../../../routes/routing-path';
import { apiActions } from '../../../../slices/api-slice';
import { ApiCheckTokenExpiry } from '../../../../api/front/wizard/api-check-token-expiry';
import { dialogActions } from '../../../dialog/slice/dialog-slice';
import { ApiPasswordResetting } from '../../../../api/front/wizard/api-staff-password-reset';
import { PasswordDescription } from '../../../ui/password/store-password-description';
import { validatePassword } from '../../../../utilities/validation/password';

type Props = {
  setStep: (step: number) => void;
  token: string,
}
type TooltipType = 'password' | 'passwordConfirm' | 'none';

export const Step3AdminPasswordResetWizard = (props: Props) => {
  const { setStep, token } = props;
  // - Hooks -
  const dispatch = useDispatch();
  // - State -
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isPasswordError, setIsPasswordError] = useState(false);
  const [isPasswordConfirmError, setIsPasswordConfirmError] = useState(false);
  const [showTooltip, setShowTooltip] = useState<TooltipType>('none');
  // - Handlers -
  // -- バリデーション --
  const handlerValidation = useCallback(() => {
    const isPasswordValid = validatePassword(password);
    const isPasswordConfirmValid =
      validatePassword(passwordConfirm) && password === passwordConfirm;

    setIsPasswordError(!isPasswordValid);
    setIsPasswordConfirmError(!isPasswordConfirmValid);
    return isPasswordValid && isPasswordConfirmValid;
  }, [password, passwordConfirm]);
  // 変更ボタン
  const handleOnClickChange = useCallback(() => {
    if (!handlerValidation()) {
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: ['入力内容に不備があります。'],
        buttons: [
          {
            label: 'OK',
            callback: () => {
              dispatch(dialogActions.pop());
            },
          },
        ],
      }));
      return;
    }
    dispatch(apiActions.run(
      new ApiPasswordResetting({
        userType: false,
        token,
        password,
        password_confirmation: passwordConfirm,
      }),
      {
        onSuccess: () => {
          setStep(4);
        },
        onBasicError: (error) => {
          dispatch(dialogActions.pushMessage({
            title: '確認',
            message: error.messages,
            buttons: [
              {
                label: 'OK',
                callback: () => {
                  dispatch(dialogActions.pop());
                },
              },
            ],
          }));
        },
      }
    ))
  }, [password, passwordConfirm]);
  const handlerChangePassword = useCallback((value: string) => {
    setPassword(value);
  }, []);
  const handlerChangePasswordConfirm = useCallback((value: string) => {
    setPasswordConfirm(value);
  }, []);
  const handlerToggleShowTooltip = useCallback((type: TooltipType) => {
    setShowTooltip(type);
  }, []);
  // - Effects -
  useEffect(() => {
    if (token === '') {
      dispatch(push(RoutingPath.login));
    }
    dispatch(apiActions.run(
      new ApiCheckTokenExpiry({ token }),
      {
        onSuccess: (res) => {
          if (res.error.errorCode === 404) {
            dispatch(dialogActions.pushMessage({
              title: 'エラー',
              message: ['トークンの有効期限が切れています。', 'ログイン画面に戻ります'],
              buttons: [
                {
                  label: 'OK',
                  callback: () => {
                    dispatch(dialogActions.pop());
                    dispatch(push(RoutingPath.login));
                  },
                },
              ],
            }));
          }
        },
      }),
    );
  },[]);
  return (
    <WizardContent>
      <WizardInputList>
        <WizardInput
          label="新しいパスワード（7〜15文字）"
          value={password}
          disabled={false}
          isPassword={true}
          maxLength={15}
          error={isPasswordError}
          descBottom={<PasswordDescription />}
          // tooltip={isPasswordError && showTooltip === 'password' ?
          //   { messages: ['7〜15文字で半角英数字・記号のうち2種類以上'] } : undefined}
          onChange={(e) => handlerChangePassword(e.target.value)}
          onMouseOver={() => handlerToggleShowTooltip('password')}
          onMouseLeave={() => handlerToggleShowTooltip('none')}
        />
        <WizardInput
          label="新しいパスワード（再入力）"
          value={passwordConfirm}
          disabled={false}
          isPassword={true}
          maxLength={15}
          error={isPasswordConfirmError}
          tooltip={isPasswordConfirmError && showTooltip === 'passwordConfirm'?
            { messages: ['パスワードが一致しません'] } : undefined}
          onChange={(e) => handlerChangePasswordConfirm(e.target.value)}
          onMouseOver={() => handlerToggleShowTooltip('passwordConfirm')}
          onMouseLeave={() => handlerToggleShowTooltip('none')}
        />
      </WizardInputList>
      <WizardButtonList>
        <WizardButton
          label="変更"
          onClick={handleOnClickChange}
        />
      </WizardButtonList>
    </WizardContent>
  );
};